import React, { useContext, useEffect, useRef, useState } from "react"
import { LogOut, Settings, Terminal, WifiOff } from "lucide-react"
import { useNavShell } from "@stjeans/shared-frontend/nav"
import { useRequestNavigate } from "@stjeans/shared-frontend/common"
import {
  FooterItem,
  SidebarEnvHeaderText,
  SidebarLastUpdated,
  StatusNoticeItem,
} from "@stjeans/shared-frontend/layout"
import AppContext from "../../AppContext.jsx"
import dbClient from "../../db/client.js"
import auth from "../../db/auth.js"
import { buildNumber } from "../../buildNumber.js"
import { useConsoleViewer } from "../../contexts/ConsoleViewerContext.jsx"
import UpdateAvailable from "./UpdateAvailable.jsx"

export default function FooterItems() {
  const { app, setApp: rawSetApp } = useContext(AppContext)
  const setApp = /** @type {any} */ (rawSetApp)
  const requestNavigate = useRequestNavigate()
  const { closeSidebar, isCollapsed } = useNavShell()
  const { showConsoleViewer } = useConsoleViewer()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef(null)
  const toggleButtonRef = useRef(null)
  const [queuedRequestsCount, setQueuedRequestsCount] = useState(0)
  const [hasQueuedRequests, setHasQueuedRequests] = useState(false)

  useEffect(() => {
    if (!isMenuOpen) return

    /** @param {MouseEvent | PointerEvent} e */
    function onGlobalPointerDown(e) {
      const menuEl = menuRef.current
      const toggleEl = toggleButtonRef.current
      const target = /** @type {any} */ (e.target)

      if (menuEl && menuEl.contains?.(target)) return
      if (toggleEl && toggleEl.contains?.(target)) return

      setIsMenuOpen(false)
    }

    function onKeyDown(e) {
      if (e.key === "Escape") {
        setIsMenuOpen(false)
      }
    }

    // Capture phase ensures we see the event even if other handlers stop propagation.
    document.addEventListener("pointerdown", onGlobalPointerDown, true)
    window.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onGlobalPointerDown, true)
      window.removeEventListener("keydown", onKeyDown)
    }
  }, [isMenuOpen])

  useEffect(() => {
    let cancelled = false
    async function checkQueuedRequests() {
      try {
        const locationId = await auth.getLocationIdAsync()
        if (!locationId) {
          if (!cancelled) {
            setHasQueuedRequests(false)
            setQueuedRequestsCount(0)
          }
          return
        }

        const db = await dbClient.open()
        const range = IDBKeyRange.bound([locationId, "0000-00-00T00:00:00.000Z"], [locationId, new Date().toISOString()])
        const count = await db.countFromIndex("api_queue", "location_id, date", range)
        if (!cancelled) {
          setHasQueuedRequests(count > 0)
          setQueuedRequestsCount(count)
        }
      } catch (e) {
        console.error("Error checking queued requests:", e)
        if (!cancelled) {
          setHasQueuedRequests(false)
          setQueuedRequestsCount(0)
        }
      }
    }

    checkQueuedRequests()
    const interval = window.setInterval(checkQueuedRequests, 30 * 1000)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [])

  async function onLogOutClick() {
    await auth.clear()
    closeSidebar()
    requestNavigate("/log-in")
  }

  function closeAdvancedMenu() {
    setIsMenuOpen(false)
  }

  function onAdvancedClick() {
    setIsMenuOpen((prev) => !prev)
  }

  function onNavigateAndClose(path) {
    requestNavigate(path)
    closeSidebar()
    closeAdvancedMenu()
  }

  function onRecordLogsToggle() {
    const newValue = !(app?.recordLogs ?? false)
    localStorage.setItem("recordLogs", newValue ? "true" : "false")
    setApp((prev) => ({ ...prev, recordLogs: newValue }))
  }

  function advancedMenuElements({ align = "right" }) {
    const placementStyles = align === "left"
      ? { left: "100%", bottom: 0, marginLeft: "0.5rem" }
      : { left: "100%", bottom: 0, marginLeft: "0.5rem" }

    if (!isMenuOpen) return null

    return (
      <>
        <div
          className="lodge-advanced-menu position-absolute bg-white text-dark shadow rounded-3 overflow-hidden"
          ref={menuRef}
          style={{
            ...(placementStyles ?? {}),
            minWidth: isCollapsed ? "220px" : "260px",
            zIndex: 10001,
          }}
          role="menu"
        >
          <button
            type="button"
            className="lodge-advanced-menu-item"
            onClick={() => {
              showConsoleViewer()
              closeSidebar()
              closeAdvancedMenu()
            }}
            role="menuitem"
          >
            <span className="d-inline-flex align-items-center gap-2">
              <Terminal size={16} />
              View Console Logs
            </span>
          </button>

          <div className="lodge-advanced-menu-sep" />

          <button
            type="button"
            className="lodge-advanced-menu-item"
            onClick={() => onNavigateAndClose("/v2/offline-queue")}
            role="menuitem"
          >
            <span className="d-inline-flex align-items-center justify-content-between w-100">
              <span>View Offline Queue</span>
              <span className={"badge " + (hasQueuedRequests ? "bg-primary" : "bg-secondary")}>
                {queuedRequestsCount}
              </span>
            </span>
          </button>

          <div className="lodge-advanced-menu-sep-strong" />

          <button
            type="button"
            className="lodge-advanced-menu-item lodge-advanced-menu-item-row"
            onClick={onRecordLogsToggle}
            role="menuitem"
          >
            <span>Record Logs</span>
            <div className="form-check form-switch m-0">
              <input
                className="form-check-input"
                type="checkbox"
                checked={Boolean(app?.recordLogs)}
                onChange={onRecordLogsToggle}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </button>

          {isCollapsed ? (
            <>
              <div className="lodge-advanced-menu-sep-strong" />
              <button
                type="button"
                className="lodge-advanced-menu-item"
                onClick={onLogOutClick}
                role="menuitem"
              >
                <span className="d-inline-flex align-items-center gap-2">
                  <LogOut size={16} />
                  Log Out
                </span>
              </button>
            </>
          ) : null}
        </div>
      </>
    )
  }

  return (
    <>
      {isCollapsed ? (
        <div className="position-relative w-100">
          <button
            ref={toggleButtonRef}
            type="button"
            onClick={onAdvancedClick}
            className="btn text-white w-100 d-flex align-items-center justify-content-center border-0"
            style={{
              fontSize: "0.875rem",
              fontWeight: 500,
              padding: "0.5rem 0.5rem",
              borderRadius: "0.5rem",
              opacity: 0.95,
            }}
            title="Settings"
          >
            <Settings size={24} />
          </button>

          {advancedMenuElements({ align: "right" })}
        </div>
      ) : (
        <>
          <div className="position-relative w-100">
            <button
              ref={toggleButtonRef}
              type="button"
              onClick={onAdvancedClick}
              className="btn text-white w-100 d-flex align-items-center border-0"
              style={{
                fontSize: "0.875rem",
                fontWeight: 500,
                padding: "0.75rem 1rem",
                borderRadius: "0.5rem",
                opacity: 0.95,
              }}
              title=""
            >
              <Settings size={20} />
              <span className="nav-text ms-2">Advanced</span>
            </button>
            {advancedMenuElements({ align: "left" })}
          </div>
          <FooterItem label="Log Out" Icon={LogOut} onClick={onLogOutClick} />
        </>
      )}

      {!app.connected ? (
        <StatusNoticeItem
          label="Trouble connecting to server"
          Icon={WifiOff}
          bgColor="#dc3545"
        />
      ) : null}

      <UpdateAvailable placement="sidebar" />

      <SidebarEnvHeaderText isCollapsed={isCollapsed} />

      <SidebarLastUpdated buildNumber={buildNumber} isCollapsed={isCollapsed} />
    </>
  )
}

